/**
 * Ticket Virginity Helpers
 * 
 * OPTIBAY MASTER RULEBOOK COMPLIANCE:
 * Provides the SINGLE CANONICAL SOURCE for determining ticket virginity state.
 * 
 * CANONICAL DEFINITION:
 * - A ticket is "VIRGIN" if: actually_started !== true
 *   (Timer has NEVER run, even if ticket is scheduled)
 * 
 * - A ticket is "DEVIRGINIZED" if: actually_started === true
 *   (Timer has run at least once, even if now stopped/paused/completed)
 * 
 * WHY THIS MATTERS:
 * - Virgin tickets can NEVER be scheduled in the past
 * - Virgin tickets MUST re-validate against no-past-time guards on any mutation
 * - Re-virginization happens when a ticket is reassigned (drops back to virgin rules)
 * - Devirginized tickets retain historical start but cannot be MOVED into the past
 * 
 * DO NOT infer virginity from any other combination of fields.
 * Always use these helper functions for consistency.
 */

export interface VirginityCheckFields {
  actually_started?: boolean | null;
  timer_status?: string | null;
  status?: string | null;
}

/**
 * Check if a ticket is in virgin state (never started).
 * Virgin tickets are subject to full no-past-time validation.
 */
export function isTicketVirgin(ticket: VirginityCheckFields): boolean {
  return ticket.actually_started !== true;
}

/**
 * Check if a ticket is devirginized (has been started at least once).
 * Devirginized tickets have historical context but still cannot be MOVED into the past.
 */
export function isTicketDevirginized(ticket: VirginityCheckFields): boolean {
  return ticket.actually_started === true;
}

/**
 * Get virginity state as a string label for debugging/logging.
 */
export function getVirginityLabel(ticket: VirginityCheckFields): 'virgin' | 'devirginized' {
  return isTicketVirgin(ticket) ? 'virgin' : 'devirginized';
}

/**
 * Determine if a ticket should be re-virginized on reassignment.
 * 
 * RULEBOOK FIX 5: Per Master Rulebook:
 * - When a ticket is moved to a different technician, it SHOULD be re-virginized
 * - Re-virginization resets SCHEDULING BEHAVIOR fields (actually_started, timer_status, etc.)
 * - Historical analytics (run_long_minutes, accumulated_bump_minutes) are PRESERVED
 * 
 * Returns true if ticket should reset to virgin state for scheduling.
 */
export function shouldRevirginizeOnReassign(
  ticket: VirginityCheckFields,
  originalTechId: string | null,
  newTechId: string | null
): boolean {
  // Only consider re-virginization if tech is actually changing
  if (originalTechId === newTechId) return false;
  
  // RULEBOOK: On reassignment to a different tech, ticket becomes virgin again
  // This means it will be subject to no-past-time validation on the new tech
  return true;
}

/**
 * Validate that a virgin ticket is not being scheduled in the past.
 * Returns an error message if validation fails, null if OK.
 * 
 * @param ticket - Ticket with virginity fields
 * @param proposedStartMin - Proposed start time in minutes since midnight
 * @param currentMin - Current time in minutes since midnight
 * @param allowFutureDate - Set true if scheduling for a future date (bypass check)
 */
export function validateVirginNoPastTime(
  ticket: VirginityCheckFields,
  proposedStartMin: number,
  currentMin: number,
  allowFutureDate: boolean = false
): string | null {
  // Future date always OK
  if (allowFutureDate) return null;
  
  // Devirginized tickets are not subject to virgin past-time rules
  // (They have their own movement rules)
  if (isTicketDevirginized(ticket)) return null;
  
  // Virgin ticket cannot start in the past
  if (proposedStartMin < currentMin) {
    return `Virgin ticket cannot be scheduled in the past (proposed ${proposedStartMin} < now ${currentMin})`;
  }
  
  return null;
}

/**
 * Edge function compatible version using simple object.
 * Import this in Deno edge functions.
 */
export const virginityHelpers = {
  isVirgin: (ticket: { actually_started?: boolean | null }) => ticket.actually_started !== true,
  isDevirginized: (ticket: { actually_started?: boolean | null }) => ticket.actually_started === true,
  getLabel: (ticket: { actually_started?: boolean | null }) => 
    ticket.actually_started !== true ? 'virgin' : 'devirginized',
};
